import * as React from 'react';
import {useEffect, useRef, useState} from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from "react-router-dom";
import { POST_MESSAGE_CONTENT_MESSAGE, TRANSACTION_STATUS, POLLING_TIME } from "../common/constants";
import { getTransactionResult, getPaymentCredentials, updateTransactionStatus } from "../libs/GsmpiApi";
import QrCodeWithLogo from "qrcode-with-logos";
import { useSelector } from "react-redux";
import { timeFormat } from "../utils/timeFormat";
import payLogoForQr from "../assets/samsung_pay_logo.png";
import styles from "../css/pc/AccountBindingByQrCodeTab.module.css";
import classNames from "classnames/bind";
import { clearInterval, setInterval } from 'worker-timers';
import debug from 'debug';

const cx = classNames.bind(styles);
const log = debug('app:AccountBindingByQrCodeTab');

export default function AccountBindingByQrCodeTab() {
  const {t, i18n} = useTranslation();
  const navigate = useNavigate();

  const [statusUpdateCountDown, setStatusUpdateCountDown] = useState(POLLING_TIME.QR_POLL);
  const [statusPollingCountDown, setStatusPollingCountDown] = useState(POLLING_TIME.QR_POLL);

  const {transactionId, serviceId, clientId} = useSelector((state) => state.paymentClient);

  const abortControllersRef = useRef([]);
  const qrTimerInfo = useRef(null);

  useEffect(() => {
    new QrCodeWithLogo({
      canvas: document.getElementById('qrCanvas'),
      content: createQrCodeUrl(),
      image: document.getElementById('qrImage'),
      width: 150,
      logo: {
        src: payLogoForQr,
        logoSize: 0.20,
        borderSize: 0.01,
        borderRadius: 15
      }
    });
  }, []);

  useEffect(() => {
    // Countdown responsible for updating the transaction's status to expired when the timer reaches 0
    if (statusUpdateCountDown > 0) {
      const timerInterval = setInterval(() => {
        setStatusUpdateCountDown(prevCountDown => prevCountDown > 0 ? prevCountDown - 1 : 0);
      }, 1000);

      return () => clearInterval(timerInterval);
    } else {
      cancelPollingRequests();
      const timedOutPayload = {
        payment: {
          status: TRANSACTION_STATUS.EXPIRED,
          reason: 'transactionExpired'
        },
        timestamp: new Date().getTime()
      };

      updateTransactionStatus(transactionId, timedOutPayload)
        .then(() => {
          navigate('/gsmpi-web/timeout', {
            state: {
              timedOutType: POST_MESSAGE_CONTENT_MESSAGE.PAYMENT_TIMEOUT
            }
          });
        })
        .catch((error) => {
          log(`Error occurred when updating the transaction status: ${error}`);

          navigate('/gsmpi-web/timeout', {
            state: {
              timedOutType: POST_MESSAGE_CONTENT_MESSAGE.PAYMENT_TIMEOUT
            }
          });
        });
    }
  }, [statusUpdateCountDown]);

  useEffect(() => {
    const abortController = new AbortController();
    abortControllersRef.current.push(abortController);
    const abortSignal = abortController.signal;

    // Countdown that continuously polls the status of the transaction then does redirection based on the polled status
    if (statusPollingCountDown > 0) {
      const pollingInterval = setInterval(() => {
        setStatusPollingCountDown(prevStatusPollingCountDown => prevStatusPollingCountDown > 0 ? prevStatusPollingCountDown - 1.5 : 0);

        getTransactionResult(transactionId, false, abortSignal)
          .then(result => {
            handlePollingResult(result);
          })
          .catch(error => {
            handlePollingError(error);
          });
      }, 1500);

      return () => clearInterval(pollingInterval);
    }
  }, [statusPollingCountDown]);

  useEffect(() => {
    document.fonts.ready.then(() => {
      if (qrTimerInfo.current.clientHeight > 45) {
        qrTimerInfo.current.style.fontSize = "14px";
      }
    });
  }, []);

  function createQrCodeUrl() {
    const params = new URLSearchParams();
    params.append('clientId', clientId);
    params.append('isFromQr',"true");

    return `${window.location.origin}/gsmpi-web/mobile/bridge?${params.toString()}`;
  }

  const cancelPollingRequests = () => {
    abortControllersRef.current.forEach(controller => controller.abort());
    abortControllersRef.current = [];
  };

  const handlePollingResult = (result) => {
    if (result.data.status === TRANSACTION_STATUS.CONFIRMED) {
      cancelPollingRequests();
      handleConfirmedTransaction(result.data.refId);
      setStatusPollingCountDown(0);
    } else if (result.data.status === TRANSACTION_STATUS.EXPIRED) {
      navigate('/gsmpi-web/timeout', {
        state: {
          timedOutType: POST_MESSAGE_CONTENT_MESSAGE.PAYMENT_TIMEOUT
        }
      });
    } else if (result.data.status === TRANSACTION_STATUS.CANCELED) {
      navigate('/gsmpi-web/cancel');
    } else if (result.data.status === TRANSACTION_STATUS.ERRED) {
      navigate('/gsmpi-web/error');
    } else if (result.data.status === TRANSACTION_STATUS.APP_UPDATE_REQUIRED) {
      navigate(`/gsmpi-web/update`);
    } else {
      log(`result status : ${result.data.status}`);
    }
  };

  const handlePollingError = (error) => {
    if (error.response.data.resultCode === 'OPM2N1101' || error.response.data.resultCode === 'OPM1N1039') {
      //transaction is expired
      navigate('/gsmpi-web/timeout', {
        state: {
          timedOutType: POST_MESSAGE_CONTENT_MESSAGE.PAYMENT_TIMEOUT
        }
      });
    } else if (error.response.data.resultCode === 'OPM2N1103' || error.response.data.resultCode === 'OPM2N1104') {
      //transaction is invalid
      navigate('/gsmpi-web/error');
    } else if (error.response.data.resultCode === 'OPM1N1046') {
      //app needs update
      navigate(`/gsmpi-web/update`);
    } else {
      navigate('/gsmpi-web/error');
    }
  };

  function handleConfirmedTransaction(refId) {
    getPaymentCredentials({refId: refId, serviceId: serviceId})
      .then((result) => {
        const state = {
          state: {
            paymentCredential: result.data,
            referenceId: refId,
          }
        };

        navigate('/gsmpi-web/done', state);
      })
      .catch(() => navigate('/gsmpi-web/error'));
  }

  return (
    <div className={cx('mpi-tab')}>
      <div className={'mpi-tab-cont'}>
        <div className={'mpi-content qr-div'}>
          <canvas id="qrCanvas" className={cx('mpi-qr-canvas')}>
            <img id="qrImage" alt="generatedQRCode"/>
          </canvas>
        </div>
        <div className={'mpi-content'}>
          <p className={cx('mpi-qr-timer-text')}>
            {t('pay.gsmpi.main.validityPeriod')}
          </p>
          <div className={cx('mpi-qr-timer-container')}>
            <div id="viewTimer"> {timeFormat(statusUpdateCountDown)} </div>
          </div>
        </div>
        <div className={cx('mpi-content mpi-pc-qr-boxbottom')}>
          <div ref={qrTimerInfo} className={cx('mpi-qr-timer-info')} lang={i18n.language}>
            <p>
              {t('pay.gsmpi.main.waitForVerification')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}